import React, {useCallback, useContext, useEffect, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Contexts
import {NotificationContext} from "../../contexts/notify.js";

// Utils
import {toTitleCase} from "../../utils/helpers.js";
import {getIcon} from "../schedule/helpers.js";

// Components
import EventList from "./EventList.js";

// Style
import {flex} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {pad} from "../../style/components/variables.js";
import {HeadingMedium, Span} from "../../style/components/general.js";

// Socket Constants

const EventsDue = ({room, locks, setHasUpdates}) => {
  const isMounted = useMountedState();

  const {notifications} = useContext(NotificationContext);

  const {api: apiEventTypes} = useApi("event-types");
  const {api: apiAddress, data: markers} = useApi("addresses");

  const [eventTypes, setEventTypes] = useState(null);
  const [hide, setHide] = useState(null);

  const getEventTypes = useCallback(
    () =>
      apiEventTypes.callGet().then(({status, data}) => {
        if (status === 200) setEventTypes(data);
      }),
    [apiEventTypes]
  );

  // Initial Load
  useEffect(() => {
    if (isMounted() && eventTypes === null) {
      getEventTypes();
      apiAddress.callGet(null, {markers: true});
    }
  }, [isMounted, eventTypes, getEventTypes, apiAddress]);

  return (
    locks &&
    notifications?.eventsDue && (
      <Events>
        {eventTypes?.map(({name, color, icon}) => {
          const retrievedIcon = getIcon(icon);

          return (
            <Glance key={name} hide={!hide || hide?.includes(name)}>
              <HeadingMedium>
                {notifications.eventsDue[name]?.length}
                &nbsp;
                <Span color={`#${color}`}>
                  {toTitleCase(name)}
                  {notifications.eventsDue[name]?.length > 1 ? "s" : ""}
                </Span>
                &nbsp;
                {retrievedIcon && <Icon color={`#${color}`} icon={retrievedIcon} />}
                &nbsp;Upcoming
              </HeadingMedium>
              <EventList
                room={room}
                type={name}
                markers={markers}
                locks={locks}
                events={notifications.eventsDue[name]}
                setHide={setHide}
                setHasUpdates={setHasUpdates}
              />
            </Glance>
          );
        })}
      </Events>
    )
  );
};

EventsDue.propTypes = {
  room: PropTypes.string.isRequired,
  locks: PropTypes.objectOf(PropTypes.any),
  setHasUpdates: PropTypes.func.isRequired
};

// Style Overrides
const Events = styled.div`
  ${flex("row", "wrap", "start", "start")};
`;

const Glance = styled.div`
  margin-bottom: ${pad}px;
  width: 100%;

  ${({hide}) =>
    hide &&
    css`
      display: none;
    `}
`;

const Icon = styled(FontAwesomeIcon)`
  ${voice.medium};
  margin-bottom: 3px;
  fill: ${({color, theme}) => color || theme.tertiary};
`;

export default EventsDue;
